/**
 * Format currency value using the provided currency format or fallback to USD
 * Supports both US formatting (12,345.67) and European formatting (12.345,67)
 * Handles multi-character symbols (USD, CHF) and formats with zero decimals (¥#,##0)
 */
export function formatCurrency(
  value: number,
  currencyFormat?: string
): string {
  if (currencyFormat) {
    // Extract currency symbol by removing all number formatting characters
    // Handles multi-char symbols like "USD", "CHF" better than matching first char only
    const currencySymbol = currencyFormat.replace(/[#,.\d0\s]+/g, '').trim() || '$';

    // Find decimal separator by checking which comes LAST (. or ,)
    // This handles formats like "$#,##0.00" where both separators exist
    const lastCommaPos = currencyFormat.lastIndexOf(',0');
    const lastPeriodPos = currencyFormat.lastIndexOf('.0');

    let decimalSeparator: string;
    let thousandsSeparator: string;
    let decimalPlaces: number;

    if (lastCommaPos > lastPeriodPos) {
      // Comma is decimal separator (European: "€ #.##0,00")
      const match = currencyFormat.match(/,0+/);
      decimalPlaces = match ? match[0].length - 1 : 0;
      decimalSeparator = ',';
      thousandsSeparator = '.';
    } else if (lastPeriodPos > lastCommaPos) {
      // Period is decimal separator (US: "$#,##0.00")
      const match = currencyFormat.match(/\.0+/);
      decimalPlaces = match ? match[0].length - 1 : 0;
      decimalSeparator = '.';
      thousandsSeparator = ',';
    } else {
      // No decimal separator (e.g., Japanese Yen: "¥#,##0")
      decimalPlaces = 0;
      decimalSeparator = '.'; // Not used when decimalPlaces = 0, but required for TypeScript
      thousandsSeparator = currencyFormat.includes(',') ? ',' : '.';
    }

    // Format the number with appropriate separators
    const parts = value.toFixed(decimalPlaces).split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    const decimalPart = parts[1];

    const formattedNumber = decimalPart
      ? `${integerPart}${decimalSeparator}${decimalPart}`
      : integerPart;

    return `${currencySymbol}${formattedNumber}`;
  }

  // Fallback to USD formatting if no currencyFormat provided
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}
